import { Box, Typography, Stack } from '@mui/material';

import {
    getDocumentTypesFromAnswers,
    getDocumentTypeDisplayName,
    getDocumentTypeDescription,
} from '../utils/documentTypeMapping';

import { type QuestionnaireAnswers } from './UploadDocumentsQuestionnaire';
import DocumentItem, { type DocumentType } from './DocumentItem';

import { useTransactionStartDate } from 'modules/common/queries/transactionStartDate';
import { useMember } from 'modules/common/queries/member';
import useStatementVerificationEnabled from 'modules/Onboarding/AccountingSetup/StatementUpload/hooks/useStatementVerificationEnabled';
import VerifiedStatementUploadSection from 'modules/Onboarding/AccountingSetup/StatementUpload/VerifiedStatementUploadSection';

interface DocumentUploadListProps {
    questionnaireAnswers: QuestionnaireAnswers;
    businessAccounts: Array<{
        id: string;
        name: string;
        institution_name: string;
        institution_id?: string;
        mask?: string;
    }>;
    uploadedDocuments: Record<string, any[]>; // Array of uploaded document objects per document type
    skippedDocuments: Record<string, boolean>;
    onUpload: (documentId: string) => void;
    onSkip: (documentId: string, skipped: boolean) => void;
    onDeleteFile?: (documentId: string, fileId: string) => void; // For file deletion
    /** Whether either bank statements section renders, verified or legacy. */
    includeStatements?: boolean;
    appearance?: 'default' | 'redesign';
}

// Helper function to get transaction date range
const getTransactionDateRange = (transactionStartDate?: string): string => {
    const now = new Date();
    const currentMonth = now.toLocaleDateString('en-US', { month: 'long' });
    const currentYear = now.getFullYear();

    if (!transactionStartDate) {
        // Fallback to current month if no transaction start date
        return `${currentMonth} ${currentYear} to ${currentMonth} ${currentYear}`;
    }

    try {
        const startDate = new Date(transactionStartDate);
        const startMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
        const startYear = startDate.getFullYear();

        // If same year and month, show just the month
        if (startYear === currentYear && startMonth === currentMonth) {
            return `${startMonth} ${startYear}`;
        }

        // Otherwise show the range
        return `${startMonth} ${startYear} to ${currentMonth} ${currentYear}`;
    } catch (error) {
        // Fallback to current month if date parsing fails
        return `${currentMonth} ${currentYear} to ${currentMonth} ${currentYear}`;
    }
};

export default function DocumentUploadList({
    questionnaireAnswers,
    businessAccounts,
    uploadedDocuments,
    skippedDocuments,
    onUpload,
    onSkip,
    onDeleteFile = () => {},
    includeStatements = true,
    appearance = 'default',
}: DocumentUploadListProps) {
    const isStatementVerificationEnabled = useStatementVerificationEnabled();
    const showVerifiedStatements = includeStatements && isStatementVerificationEnabled;
    const showLegacyStatements = includeStatements && !isStatementVerificationEnabled;
    const { data: member } = useMember();
    const { data: transactionStartDateData } = useTransactionStartDate(member?.id);
    // Generate business documents based on questionnaire answers
    const generateBusinessDocuments = (): DocumentType[] => {
        const documentTypes = getDocumentTypesFromAnswers(questionnaireAnswers);

        // Filter out bank statements as they're handled separately
        const businessDocumentTypes = documentTypes.filter((type) => type !== 'BUSINESS_BANK_STATEMENT');

        return businessDocumentTypes.map((documentType, index) => ({
            id: `business-doc-${index}`,
            name: getDocumentTypeDisplayName(documentType),
            description: getDocumentTypeDescription(documentType),
            required: true,
            documentType,
        }));
    };

    // Generate bank statement documents
    const generateBankStatementDocuments = (): DocumentType[] => {
        const bankDocs = businessAccounts.map((account) => ({
            id: `bank-statement-${account.id}`,
            name: 'Bank Statement',
            description: `Most recent bank statement for ${account.name} (${account.institution_name})`,
            required: true,
            documentType: 'BUSINESS_BANK_STATEMENT',
            bankAccountId: account.id,
            bankAccountName: `${account.name} (${account.institution_name})`,
            institutionName: account.institution_name,
            institutionId: account.institution_id,
            mask: account.mask,
        }));
        return bankDocs;
    };

    const businessDocuments = generateBusinessDocuments();
    const bankStatementDocuments = generateBankStatementDocuments();

    // With verification on, the statement section enumerates accounts from coverage rather
    // than from confirmed_business_accounts, so it must get a chance to render (and to show
    // its own empty state) even when this list has nothing.
    const hasAnySection =
        businessDocuments.length > 0 ||
        (showLegacyStatements && bankStatementDocuments.length > 0) ||
        showVerifiedStatements;

    if (!hasAnySection) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                    No documents required based on your answers.
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={4} sx={{ width: '100%' }}>
            {/* Business Documents Section */}
            {businessDocuments.length > 0 && (
                <Box>
                    {appearance !== 'redesign' && (
                        <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                            Business documents
                        </Typography>
                    )}

                    {/* Custom styled container matching Figma */}
                    <Box
                        sx={{
                            borderRadius: '12px',
                            border: 1,
                            borderColor: 'grey.100',
                            overflow: 'hidden',
                        }}>
                        {businessDocuments.map((document, index) => (
                            <Box
                                key={document.id}
                                sx={{
                                    p: '20px 24px',
                                    borderBottom: index < businessDocuments.length - 1 ? 1 : 'none',
                                    borderBottomColor: 'grey.100',
                                }}>
                                <DocumentItem
                                    document={document}
                                    uploadedDocuments={uploadedDocuments[document.id]}
                                    isSkipped={skippedDocuments[document.id] || false}
                                    onUpload={onUpload}
                                    onSkip={onSkip}
                                    onDeleteFile={onDeleteFile}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Bank Statements Section — verified, coverage-driven */}
            {showVerifiedStatements && <VerifiedStatementUploadSection />}

            {/* Bank Statements Section — legacy "most recent statement" rows */}
            {showLegacyStatements && bankStatementDocuments.length > 0 && (
                <Box>
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                        <Typography variant="h6" color="text.primary">
                            Bank statements from{' '}
                            {getTransactionDateRange(transactionStartDateData?.transaction_start_date || undefined)}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            These ensure complete and accurate financial records
                        </Typography>
                    </Stack>

                    {/* Custom styled container matching Figma */}
                    <Box
                        sx={{
                            borderRadius: '12px',
                            border: 1,
                            borderColor: 'grey.100',
                            overflow: 'hidden',
                        }}>
                        {bankStatementDocuments.map((document, index) => (
                            <Box
                                key={document.id}
                                sx={{
                                    p: '24px',
                                    borderBottom: index < bankStatementDocuments.length - 1 ? 1 : 'none',
                                    borderBottomColor: 'grey.100',
                                }}>
                                <DocumentItem
                                    document={document}
                                    uploadedDocuments={uploadedDocuments[document.id]}
                                    isSkipped={skippedDocuments[document.id] || false}
                                    onUpload={onUpload}
                                    onSkip={onSkip}
                                    onDeleteFile={onDeleteFile}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Stack>
    );
}
